import { useEffect, useState } from "react";
import { api, fmtCurrency, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Euro, Mail } from "lucide-react";
import { toast } from "sonner";

const STATUS_COLORS = {
  draft: "bg-neutral-100 text-neutral-700",
  open: "bg-blue-100 text-blue-700",
  partially_paid: "bg-amber-100 text-amber-700",
  paid: "bg-emerald-100 text-emerald-700",
  overdue: "bg-red-100 text-red-700",
};
const STATUS_LABELS = { draft: "Entwurf", open: "Offen", partially_paid: "Teilw. bezahlt", paid: "Bezahlt", overdue: "Überfällig" };

const empty = {
  customer_id: "", project_id: "", invoice_number: "", invoice_date: "",
  due_date: "", service_period: "", net_amount: 0, vat_rate: 19,
  agency_fee_part: 0, media_budget_part: 0, status: "open", notes: "",
};

const emptyPay = { customer_id: "", project_id: "", account_id: "", amount: 0, payment_date: "", payment_type: "milestone", notes: "" };

export default function OutgoingInvoicesPage() {
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [payOpen, setPayOpen] = useState(null);
  const [payForm, setPayForm] = useState(emptyPay);
  const [dunningOpen, setDunningOpen] = useState(null);
  const [dunningForm, setDunningForm] = useState({ subject: "", message: "", attach: true });

  const load = async () => {
    const [inv, cu, pr, ac] = await Promise.all([
      api.get("/outgoing-invoices"), api.get("/customers"), api.get("/projects"), api.get("/accounts"),
    ]);
    setItems(inv.data); setCustomers(cu.data); setProjects(pr.data); setAccounts(ac.data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    const payload = {
      ...form,
      net_amount: Number(form.net_amount), vat_rate: Number(form.vat_rate),
      agency_fee_part: Number(form.agency_fee_part), media_budget_part: Number(form.media_budget_part),
      project_id: form.project_id || null,
    };
    try {
      if (editing) await api.patch(`/outgoing-invoices/${editing.id}`, payload);
      else await api.post("/outgoing-invoices", payload);
      toast.success(editing ? "Rechnung aktualisiert" : "Rechnung angelegt");
      setOpen(false); setEditing(null); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const del = async (id) => {
    if (!window.confirm("Rechnung wirklich löschen?")) return;
    await api.delete(`/outgoing-invoices/${id}`); toast.success("Gelöscht"); load();
  };

  const addPayment = async () => {
    try {
      await api.post(`/outgoing-invoices/${payOpen.id}/payments`, {
        ...payForm, amount: Number(payForm.amount), customer_id: payOpen.customer_id,
        project_id: payOpen.project_id || null, account_id: payForm.account_id || null,
        invoice_id: payOpen.id,
      });
      toast.success("Zahlung erfasst");
      setPayOpen(null); setPayForm(emptyPay); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Fehler"); }
  };

  const sendDunning = async () => {
    if (!dunningOpen) return;
    try {
      await api.post("/dunning/send", {
        invoice_id: dunningOpen.id,
        subject: dunningForm.subject,
        message: dunningForm.message,
        attach_open_receivables_report: dunningForm.attach,
      });
      toast.success("Mahnung gesendet");
      setDunningOpen(null);
      setDunningForm({ subject: "", message: "", attach: true });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Senden der Mahnung");
    }
  };

  const totalOpen = items.filter(i => i.status !== "paid").reduce((s, i) => s + (i.gross_amount - i.paid_amount), 0);
  const totalPaid = items.reduce((s, i) => s + (i.paid_amount || 0), 0);

  return (
    <div className="space-y-6" data-testid="outgoing-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-neutral-500 font-semibold">Ausgangsrechnungen</div>
          <h1 className="font-display text-3xl font-semibold tracking-tight mt-1">Kundenrechnungen & Zahlungen</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(empty); } }}>
          <DialogTrigger asChild><Button data-testid="add-outgoing-btn"><Plus className="h-4 w-4 mr-2" />Neue Rechnung</Button></DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>{editing ? "Rechnung bearbeiten" : "Neue Ausgangsrechnung"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto pr-2">
              <div><Label>Rechnungsnummer</Label><Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="out-inv-number" /></div>
              <div>
                <Label>Kunde</Label>
                <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v })}>
                  <SelectTrigger data-testid="out-inv-customer"><SelectValue placeholder="Kunde wählen" /></SelectTrigger>
                  <SelectContent>{customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Projekt</Label>
                <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Kein Projekt —</SelectItem>
                    {projects.filter(p => !form.customer_id || p.customer_id === form.customer_id).map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(STATUS_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Rechnungsdatum</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></div>
              <div><Label>Fällig am</Label><Input type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} /></div>
              <div><Label>Netto gesamt (€)</Label><Input type="number" step="0.01" value={form.net_amount} onChange={(e) => setForm({ ...form, net_amount: e.target.value })} data-testid="out-inv-net" /></div>
              <div><Label>USt-Satz %</Label><Input type="number" value={form.vat_rate} onChange={(e) => setForm({ ...form, vat_rate: e.target.value })} /></div>
              <div><Label>Anteil Agenturhonorar (€)</Label><Input type="number" step="0.01" value={form.agency_fee_part} onChange={(e) => setForm({ ...form, agency_fee_part: e.target.value })} data-testid="out-inv-agency" /></div>
              <div><Label>Anteil Media Budget (€)</Label><Input type="number" step="0.01" value={form.media_budget_part} onChange={(e) => setForm({ ...form, media_budget_part: e.target.value })} data-testid="out-inv-media" /></div>
              <div className="col-span-2"><Label>Leistungszeitraum</Label><Input value={form.service_period} onChange={(e) => setForm({ ...form, service_period: e.target.value })} /></div>
              <div className="col-span-2"><Label>Notizen</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
              <div className="col-span-2 text-xs text-neutral-500 bg-neutral-50 p-3 rounded-lg">
                Hinweis: Media-Anteile sind durchlaufende Posten und werden im Cockpit aus dem frei verfügbaren Geld <strong>nicht</strong> als Agentureinnahme gewertet.
              </div>
            </div>
            <DialogFooter><Button onClick={save} data-testid="out-inv-save">{editing ? "Speichern" : "Anlegen"}</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Stat label="Offene Forderungen" value={fmtCurrency(totalOpen)} />
        <Stat label="Bereits eingenommen" value={fmtCurrency(totalPaid)} />
        <Stat label="Anzahl Rechnungen" value={items.length} />
      </div>

      <div className="rounded-xl border border-neutral-200 bg-white">
        <Table data-testid="out-inv-table">
          <TableHeader>
            <TableRow>
              <TableHead>Nr.</TableHead><TableHead>Kunde</TableHead><TableHead>Datum</TableHead><TableHead>Fällig</TableHead>
              <TableHead className="text-right">Brutto</TableHead><TableHead className="text-right">Bezahlt</TableHead>
              <TableHead>Status</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((inv) => (
              <TableRow key={inv.id} className="table-row-hover" data-testid={`out-inv-row-${inv.id}`}>
                <TableCell className="font-medium">{inv.invoice_number}</TableCell>
                <TableCell className="text-sm">{inv.customer?.company_name}</TableCell>
                <TableCell className="text-sm text-neutral-600">{fmtDate(inv.invoice_date)}</TableCell>
                <TableCell className="text-sm text-neutral-600">{fmtDate(inv.due_date)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCurrency(inv.gross_amount)}</TableCell>
                <TableCell className="text-right tabular-nums">{fmtCurrency(inv.paid_amount)}</TableCell>
                <TableCell><Badge className={STATUS_COLORS[inv.status]} variant="secondary">{STATUS_LABELS[inv.status]}</Badge>
                  {inv.dunning_stage > 0 && (
                    <Badge variant="outline" className="ml-1 text-[10px] border-red-300 text-red-700">
                      Mahnstufe {inv.dunning_stage}
                    </Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {(inv.status === "open" || inv.status === "overdue" || inv.status === "partially_paid") && (
                    <Button variant="ghost" size="icon" onClick={() => { setDunningOpen(inv); setDunningForm({ subject: `Zahlungserinnerung Rechnung ${inv.invoice_number}`, message: "", attach: true }); }} title="Mahnung senden" data-testid={`out-inv-dun-${inv.id}`}><Mail className="h-4 w-4 text-orange-500" /></Button>
                  )}
                  {inv.status !== "paid" && <Button variant="ghost" size="icon" onClick={() => { setPayOpen(inv); setPayForm({ ...emptyPay, customer_id: inv.customer_id, project_id: inv.project_id, amount: inv.gross_amount - inv.paid_amount, payment_date: new Date().toISOString().slice(0, 10) }); }} title="Zahlung erfassen" data-testid={`out-inv-pay-${inv.id}`}><Euro className="h-4 w-4" /></Button>}
                  <Button variant="ghost" size="icon" onClick={() => { setEditing(inv); setForm({ ...empty, ...inv, project_id: inv.project_id || "" }); setOpen(true); }} data-testid={`out-inv-edit-${inv.id}`}><Pencil className="h-4 w-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => del(inv.id)} data-testid={`out-inv-delete-${inv.id}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-neutral-500 py-12">Noch keine Rechnungen</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>

      {/* Payment Dialog */}
      <Dialog open={!!payOpen} onOpenChange={(v) => { if (!v) setPayOpen(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Kundenzahlung erfassen — {payOpen?.invoice_number}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Betrag (€)</Label><Input type="number" step="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })} data-testid="payment-amount" /></div>
            <div><Label>Zahlungsdatum</Label><Input type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })} data-testid="payment-date" /></div>
            <div>
              <Label>Konto</Label>
              <Select value={payForm.account_id || "none"} onValueChange={(v) => setPayForm({ ...payForm, account_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="payment-account"><SelectValue placeholder="Konto wählen" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Kein Konto —</SelectItem>
                  {accounts.map(a => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Zahlungsart</Label>
              <Select value={payForm.payment_type} onValueChange={(v) => setPayForm({ ...payForm, payment_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="anzahlung">Anzahlung</SelectItem>
                  <SelectItem value="setup">Setup-Zahlung</SelectItem>
                  <SelectItem value="retainer">Retainer</SelectItem>
                  <SelectItem value="milestone">Meilensteinzahlung</SelectItem>
                  <SelectItem value="final">Schlussrechnung</SelectItem>
                  <SelectItem value="media_refund">Media-Budget-Erstattung</SelectItem>
                  <SelectItem value="other">Sonstige Zahlung</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter><Button onClick={addPayment} data-testid="payment-save">Zahlung speichern</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dunning Dialog */}
      <Dialog open={!!dunningOpen} onOpenChange={(v) => { if (!v) setDunningOpen(null); }}>
        <DialogContent data-testid="dunning-dialog">
          <DialogHeader>
            <DialogTitle>Mahnung senden</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-neutral-600 bg-neutral-50 rounded-lg border border-neutral-200 p-3">
              An: <strong>{dunningOpen?.customer?.email || "—"}</strong><br />
              Rechnung: <strong>{dunningOpen?.invoice_number}</strong> · Offen: {dunningOpen ? ((dunningOpen.gross_amount - dunningOpen.paid_amount).toFixed(2) + " €") : "—"}
            </div>
            <div><Label>Betreff</Label><Input value={dunningForm.subject} onChange={(e) => setDunningForm({ ...dunningForm, subject: e.target.value })} data-testid="dun-subject" /></div>
            <div><Label>Zusatztext (optional)</Label><Input value={dunningForm.message} onChange={(e) => setDunningForm({ ...dunningForm, message: e.target.value })} placeholder="z.B. Bitte um zeitnahe Begleichung." /></div>
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <input type="checkbox" id="attach" checked={dunningForm.attach} onChange={(e) => setDunningForm({ ...dunningForm, attach: e.target.checked })} />
              <label htmlFor="attach">PDF „Offene Forderungen" anhängen</label>
            </div>
          </div>
          <DialogFooter><Button onClick={sendDunning} data-testid="dun-send"><Mail className="h-4 w-4 mr-2" />Jetzt senden</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-neutral-200 bg-white p-5">
      <div className="text-[11px] uppercase tracking-[0.18em] text-neutral-500 font-semibold">{label}</div>
      <div className="font-display text-2xl font-semibold mt-1">{value}</div>
    </div>
  );
}
